import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import BopBackLink from '../../../components/BopBackLink';
import { apiFetch } from '../../../lib/api';
import ModelloPagina from '../../../pubblico/ModelloPagina';
import PaginaStandard, { DatiPagina } from '../../../pubblico/PaginaStandard';
import { ModelloRender, TemaPagina } from '../../../pubblico/aspetto';
import { API_DOCS } from './AnteprimaPaginaPage.api';

interface DatiAnteprima {
  modello: ModelloRender | null;
  tema: TemaPagina;
  // Solo per l'anteprima di un cantiere: i suoi dati veri, con cui si compone la pagina standard.
  dati?: DatiPagina;
}

// Una sola pagina per due rotte: l'anteprima di un modello della libreria (con dati di esempio)
// e quella della pagina di un cantiere (con i suoi dati veri, anche se la pagina non è ancora attiva).
export default function AnteprimaPaginaPage() {
  const { id, offertaId } = useParams();
  const daModello = id !== undefined;
  const [anteprima, setAnteprima] = useState<DatiAnteprima | null>(null);
  const [errore, setErrore] = useState('');
  const [telefono, setTelefono] = useState(false);

  useEffect(() => {
    // Cambiando modello o offerta, una risposta arrivata in ritardo per quello di prima va ignorata.
    let attiva = true;
    setAnteprima(null);
    setErrore('');
    const richiesta = daModello
      // @api GET /api/percorso-acquirenti/modelli/:id/anteprima -> {"success":true,"data":{"modello":"object","tema":"object"}}
      ? apiFetch<{ success: boolean; data: DatiAnteprima }>(`/api/percorso-acquirenti/modelli/${id}/anteprima`)
      // @api GET /api/percorso-acquirenti/offerte/:offertaId/anteprima -> {"success":true,"data":{"modello":"object","tema":"object","dati":"object"}}
      : apiFetch<{ success: boolean; data: DatiAnteprima }>(`/api/percorso-acquirenti/offerte/${offertaId}/anteprima`);
    richiesta
      .then(r => { if (attiva) setAnteprima(r.data); })
      .catch(e => { if (attiva) setErrore((e as Error).message); });
    return () => { attiva = false; };
  }, [daModello, id, offertaId]);

  // Al posto della prenotazione vera (che qui non deve prenotare nulla) un riquadro fisso.
  const prenotazione = (
    <div className="bop-pubblico__riquadro-prenotazione">
      <p className="bop-pubblico__testo">Qui il cliente sceglie giorno e ora e inserisce i suoi dati</p>
    </div>
  );

  const standard = anteprima?.dati ? (
    <PaginaStandard dati={anteprima.dati} modalita="pagina" tema={anteprima.tema} prenotazione={prenotazione} anteprima />
  ) : (
    <div className="bop-alert bop-alert--warning" role="alert">Il modello non si può mostrare: controlla che contenga il segnaposto della prenotazione.</div>
  );

  return (
    <Layout>
      {daModello
        ? <BopBackLink to="/commerciale/percorso/modelli" label="Torna ai modelli pagina" />
        : <BopBackLink to={`/commerciale/capitolati/offerte/${offertaId}`} label="Torna all'offerta" />}
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Anteprima della pagina</h1>
          <p className="bop-page-subtitle">
            {daModello
              ? 'Il modello con dati di esempio. Il blocco di prenotazione qui è solo un segnaposto.'
              : 'La pagina di questo cantiere con i suoi dati, anche se non è ancora attiva. Il blocco di prenotazione qui è solo un segnaposto.'}
          </p>
        </div>
        <div className="bop-flex" role="group" aria-label="Dimensione dello schermo">
          <button type="button" className={`bop-btn ${telefono ? 'bop-btn-secondary' : 'bop-btn-primary'}`} aria-pressed={!telefono} onClick={() => setTelefono(false)}>Computer</button>
          <button type="button" className={`bop-btn ${telefono ? 'bop-btn-primary' : 'bop-btn-secondary'}`} aria-pressed={telefono} onClick={() => setTelefono(true)}>Telefono</button>
        </div>
      </div>

      {errore && <div className="bop-alert bop-alert--danger" role="alert">{errore}</div>}
      {!anteprima && !errore && <div className="bop-empty-state">Caricamento…</div>}
      {anteprima && (
        <div className={`bop-anteprima__cornice${telefono ? ' bop-anteprima__cornice--telefono' : ''}`}>
          {anteprima.modello
            ? <ModelloPagina modello={anteprima.modello} tema={anteprima.tema} prenotazione={prenotazione} standard={standard} anteprima />
            : standard}
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
